import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Target,
  Clock,
  Flame,
  Trophy,
  TrendingUp,
  TrendingDown,
  BookOpen,
  ChevronRight,
  Lightbulb,
  BarChart3,
  Calendar,
  Play,
  Zap,
  Award,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Timer,
  Brain,
  Star,
  Sparkles,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from "recharts";
import Navbar from "../components/Navbar";

// Skeleton component for loading states
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState("30");
  const [statsPeriod, setStatsPeriod] = useState("all");

  useEffect(() => {
    fetchDashboardData();
  }, [chartPeriod]);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, analyticsRes, insightsRes, historyRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/analytics?days=${chartPeriod}`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/insights`, { headers, withCredentials: true }),
        axios.get(`${API}/practice/history?limit=10`, { headers, withCredentials: true }),
      ]);

      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setInsights(insightsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate week-over-week changes (mock for now)
  const getWeeklyChange = (value) => {
    // In a real implementation, this would come from the backend
    const change = Math.floor(Math.random() * 20) - 5;
    return change;
  };

  // Calculate streak progress
  const getStreakProgress = () => {
    const current = stats?.current_streak || 0;
    if (current >= 30) return { target: 30, progress: 100, label: "30-Day Streak Achieved!" };
    if (current >= 7) return { target: 30, progress: (current / 30) * 100, label: `${30 - current} days to 30-day streak` };
    return { target: 7, progress: (current / 7) * 100, label: `${7 - current} days to 7-day streak` };
  };

  // Get difficulty label
  const getDifficultyLabel = (level) => {
    const labels = { 1: "Beginner", 2: "Intermediate", 3: "Advanced", 4: "Editorial" };
    return labels[level] || `Level ${level}`;
  };

  // Get difficulty color
  const getDifficultyColor = (level) => {
    const colors = {
      1: "bg-emerald-100 text-emerald-700",
      2: "bg-blue-100 text-blue-700",
      3: "bg-amber-100 text-amber-700",
      4: "bg-purple-100 text-purple-700"
    };
    return colors[level] || "bg-slate-100 text-slate-700";
  };

  // Sort categories by accuracy
  const getSortedCategories = () => {
    if (!analytics?.category_performance) return [];
    return [...analytics.category_performance].sort((a, b) => b.accuracy - a.accuracy);
  };

  // Get strongest and weakest categories
  const getTopCategories = () => {
    const sorted = getSortedCategories();
    return {
      strongest: sorted.slice(0, 2),
      weakest: sorted.slice(-2).reverse()
    };
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-10 w-64 mb-2" />
            <Skeleton className="h-5 w-96" />
          </div>
          
          {/* Quick Actions Skeleton */}
          <Skeleton className="h-32 w-full mb-8 rounded-2xl" />
          
          {/* Stats Grid Skeleton */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          
          {/* Charts Skeleton */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <Skeleton className="h-80 rounded-xl" />
            <Skeleton className="h-80 rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  // Empty state - no attempts yet
  const isNewUser = !stats || stats.total_attempts === 0;

  if (isNewUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Welcome Card */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-indigo-600 to-purple-700 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 text-center md:text-left">
                  <Badge className="bg-white/20 text-white mb-4">New to ReadMaster</Badge>
                  <h1 className="text-3xl md:text-4xl font-bold mb-4">
                    Welcome, {user?.name?.split(" ")[0]}! 👋
                  </h1>
                  <p className="text-lg text-indigo-100 mb-6">
                    Start your reading comprehension journey today. Complete your first practice to unlock analytics and personalized insights.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                    <Button 
                      size="lg" 
                      onClick={() => navigate("/practice")}
                      className="bg-white text-indigo-700 hover:bg-indigo-50 gap-2"
                      data-testid="first-practice-btn"
                    >
                      <Play className="w-5 h-5" />
                      Start First Practice
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => navigate("/practice?mode=school")}
                      className="border-white/30 text-white hover:bg-white/10 gap-2"
                    >
                      <Zap className="w-5 h-5" />
                      Quick 3-Question Practice
                    </Button>
                  </div>
                </div>
                <div className="hidden md:block">
                  <div className="w-48 h-48 bg-white/10 rounded-full flex items-center justify-center">
                    <BookOpen className="w-24 h-24 text-white/80" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Getting Started Steps */}
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { icon: BookOpen, title: "Choose a Passage", desc: "Select from 80+ passages across topics" },
              { icon: Target, title: "Answer Questions", desc: "Test your comprehension with MCQs" },
              { icon: TrendingUp, title: "Track Progress", desc: "See detailed analytics and insights" },
            ].map((step, idx) => (
              <Card key={idx} className="border border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  const streakProgress = getStreakProgress();
  const topCategories = getTopCategories();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(" ")[0]}! 👋
            </h1>
            <p className="text-slate-600 mt-1">Track your reading comprehension progress</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Calendar className="w-4 h-4" />
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>

        {/* Today's Practice / Quick Actions Section */}
        <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white overflow-hidden" data-testid="quick-actions-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  <span className="text-indigo-200 font-medium">Today&apos;s Practice</span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold mb-1">Ready to improve your reading skills?</h2>
                <p className="text-indigo-200">
                  {stats?.current_streak > 0 
                    ? `You're on a ${stats.current_streak}-day streak! Keep it going.`
                    : "Start a new streak today!"}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {/* Continue Practice - Resume last session or use last filters */}
                <div className="flex flex-col items-center">
                  <Button 
                    onClick={() => {
                      // Get last attempt's mode/difficulty from history
                      const lastAttempt = history[0];
                      if (lastAttempt) {
                        const mode = lastAttempt.exam_modes?.[0] || "all";
                        const difficulty = lastAttempt.difficulty || "";
                        const params = new URLSearchParams();
                        params.set("practiceMode", "resume");
                        if (mode !== "all") params.set("mode", mode);
                        if (difficulty) params.set("difficulty", String(difficulty));
                        navigate(`/practice?${params.toString()}`);
                      } else {
                        navigate("/practice");
                      }
                    }}
                    className="bg-white text-indigo-700 hover:bg-indigo-50 gap-2 shadow-lg"
                    data-testid="continue-practice-btn"
                  >
                    <Play className="w-4 h-4" />
                    Continue Practice
                  </Button>
                  <span className="text-xs text-indigo-200 mt-1">Resume where you left off</span>
                </div>
                
                {/* Daily Challenge - Today's featured passage */}
                <div className="flex flex-col items-center">
                  <Button 
                    onClick={() => navigate("/practice?practiceMode=daily")}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 gap-2"
                    data-testid="daily-challenge-btn"
                  >
                    <Trophy className="w-4 h-4 text-amber-300" />
                    Daily Challenge
                  </Button>
                  <span className="text-xs text-indigo-200 mt-1">Today&apos;s featured passage</span>
                </div>
                
                {/* Quick Practice - Short passage, few questions */}
                <div className="flex flex-col items-center">
                  <Button 
                    onClick={() => navigate("/practice?practiceMode=quick")}
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 gap-2"
                    data-testid="quick-practice-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Quick Practice
                  </Button>
                  <span className="text-xs text-indigo-200 mt-1">Short set (≤4 questions)</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Streak + Stats Row */}
        <div className="grid lg:grid-cols-5 gap-4 mb-6">
          {/* Streak Card - Takes 2 columns */}
          <Card className="lg:col-span-2 border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50" data-testid="streak-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700">Current Streak</p>
                    <p className="text-3xl font-bold text-amber-900">{stats?.current_streak || 0} <span className="text-lg font-normal">days</span></p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-amber-600">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm">Best</span>
                  </div>
                  <p className="text-xl font-bold text-amber-800">{stats?.best_streak || 0}</p>
                </div>
              </div>
              
              {/* Streak Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-700">{streakProgress.label}</span>
                  <span className="text-amber-600 font-medium">{Math.round(streakProgress.progress)}%</span>
                </div>
                <Progress value={streakProgress.progress} className="h-2 bg-amber-200" />
                <div className="flex justify-between text-xs text-amber-600">
                  <span>Day 0</span>
                  <span>{streakProgress.target}-Day Goal</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Stats - 3 columns */}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* Passages Completed */}
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="text-xs text-slate-500">Passages</div>
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_attempts || 0}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </CardContent>
            </Card>

            {/* Questions Solved */}
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="text-xs text-slate-500">Questions</div>
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.total_questions || 0}</p>
                <p className="text-xs text-slate-500">Solved</p>
              </CardContent>
            </Card>

            {/* Accuracy */}
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-sky-600" />
                  </div>
                  <div className="text-xs text-slate-500">Accuracy</div>
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats?.accuracy || 0}%</p>
                <p className="text-xs text-slate-500">Overall</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Accuracy Trend Chart */}
          <Card className="border-0 shadow-md" data-testid="accuracy-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-sky-600" />
                  Accuracy Trend
                </CardTitle>
                <div className="flex gap-1">
                  {["7", "30", "90"].map((period) => (
                    <Button
                      key={period}
                      size="sm"
                      variant={chartPeriod === period ? "default" : "ghost"}
                      className={`h-7 px-2 text-xs ${chartPeriod === period ? "bg-slate-900" : ""}`}
                      onClick={() => setChartPeriod(period)}
                    >
                      {period}D
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {analytics?.accuracy_trend && analytics.accuracy_trend.length >= 3 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={analytics.accuracy_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }} 
                      stroke="#94A3B8"
                      tickFormatter={(value) => new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      tick={{ fontSize: 11 }} 
                      stroke="#94A3B8"
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "none",
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      formatter={(value, name) => [`${value}%`, name === "accuracy" ? "Accuracy" : name]}
                      labelFormatter={(value) => new Date(value).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    />
                    <Line
                      type="monotone"
                      dataKey="accuracy"
                      stroke="#0EA5E9"
                      strokeWidth={2.5}
                      dot={{ fill: "#0EA5E9", strokeWidth: 0, r: 4 }}
                      activeDot={{ r: 6, fill: "#0EA5E9" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-52 flex flex-col items-center justify-center text-center p-6 bg-slate-50 rounded-xl">
                  <BarChart3 className="w-12 h-12 text-slate-300 mb-3" />
                  <p className="text-slate-600 font-medium">No trend data yet</p>
                  <p className="text-sm text-slate-500 mt-1">Complete 3 practices to see your accuracy trend</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate("/practice")}
                  >
                    Start Practice
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Performance */}
          <Card className="border-0 shadow-md" data-testid="category-chart">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                  Performance by Category
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {analytics?.category_performance && analytics.category_performance.length > 0 ? (
                <div className="space-y-4">
                  {/* Top/Bottom Categories Badges */}
                  {topCategories.strongest.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {topCategories.strongest.map((cat) => (
                        <Badge key={cat.category} className="bg-emerald-100 text-emerald-700 gap-1">
                          <Star className="w-3 h-3" /> Best: {cat.category} ({cat.accuracy}%)
                        </Badge>
                      ))}
                      {topCategories.weakest.length > 0 && topCategories.weakest[0].accuracy < 70 && (
                        <Badge className="bg-rose-100 text-rose-700 gap-1">
                          <Target className="w-3 h-3" /> Focus: {topCategories.weakest[0].category}
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={getSortedCategories().slice(0, 6)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                      <YAxis dataKey="category" type="category" tick={{ fontSize: 11 }} stroke="#94A3B8" width={70} />
                      <Tooltip
                        contentStyle={{
                          background: "#fff",
                          border: "none",
                          borderRadius: "12px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                        formatter={(value, name, props) => [
                          `${value}% (${props.payload.total_questions} questions)`,
                          "Accuracy"
                        ]}
                      />
                      <Bar dataKey="accuracy" radius={[0, 4, 4, 0]}>
                        {getSortedCategories().slice(0, 6).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.accuracy >= 80 ? "#10B981" : entry.accuracy >= 60 ? "#0EA5E9" : "#F59E0B"} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-52 flex flex-col items-center justify-center text-center p-6 bg-slate-50 rounded-xl">
                  <BarChart3 className="w-12 h-12 text-slate-300 mb-3" />
                  <p className="text-slate-600 font-medium">No category data yet</p>
                  <p className="text-sm text-slate-500 mt-1">Practice different categories to see performance</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Progress Summary + Insights Row */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Progress Summary by Difficulty */}
          <Card className="border-0 shadow-md" data-testid="difficulty-progress-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Award className="w-5 h-5 text-purple-600" />
                Progress by Difficulty
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analytics?.difficulty_performance && analytics.difficulty_performance.length > 0 ? (
                <div className="space-y-4">
                  {analytics.difficulty_performance.map((diff) => (
                    <div key={diff.difficulty} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Badge className={getDifficultyColor(diff.difficulty)}>
                            L{diff.difficulty}
                          </Badge>
                          <span className="text-sm text-slate-600">{getDifficultyLabel(diff.difficulty)}</span>
                        </div>
                        <span className="text-sm font-semibold text-slate-900">{diff.accuracy}%</span>
                      </div>
                      <Progress 
                        value={diff.accuracy} 
                        className="h-2" 
                      />
                      <p className="text-xs text-slate-500">{diff.total_questions} questions attempted</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-40 flex flex-col items-center justify-center text-center">
                  <Brain className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Practice to see difficulty breakdown</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Smart Insights */}
          <Card className="lg:col-span-2 border-0 shadow-md" data-testid="insights-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Smart Insights
              </CardTitle>
              <CardDescription>Personalized recommendations for improvement</CardDescription>
            </CardHeader>
            <CardContent>
              {insights.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  {insights.slice(0, 4).map((insight, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                        insight.type === "weakness"
                          ? "bg-rose-50 border-rose-200 hover:border-rose-300"
                          : insight.type === "achievement"
                          ? "bg-emerald-50 border-emerald-200 hover:border-emerald-300"
                          : "bg-sky-50 border-sky-200 hover:border-sky-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          insight.type === "weakness"
                            ? "bg-rose-100"
                            : insight.type === "achievement"
                            ? "bg-emerald-100"
                            : "bg-sky-100"
                        }`}>
                          {insight.type === "weakness" ? (
                            <Target className="w-4 h-4 text-rose-600" />
                          ) : insight.type === "achievement" ? (
                            <Trophy className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <Lightbulb className="w-4 h-4 text-sky-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 line-clamp-2">{insight.message}</p>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="mt-2 h-7 px-2 text-xs gap-1"
                            onClick={() => navigate("/practice")}
                          >
                            {insight.action}
                            <ArrowRight className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-center">
                  <Lightbulb className="w-10 h-10 text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Complete more practices to unlock personalized insights</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Practice Section */}
        <Card className="border-0 shadow-md" data-testid="history-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-slate-600" />
                Recent Practice
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate("/history")} className="gap-1">
                View All
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="space-y-2">
                {history.slice(0, 5).map((attempt) => (
                  <div
                    key={attempt.attempt_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Accuracy Badge */}
                      <div
                        className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center flex-shrink-0 ${
                          attempt.accuracy >= 80
                            ? "bg-emerald-100"
                            : attempt.accuracy >= 60
                            ? "bg-amber-100"
                            : "bg-rose-100"
                        }`}
                      >
                        <span
                          className={`text-lg font-bold ${
                            attempt.accuracy >= 80
                              ? "text-emerald-600"
                              : attempt.accuracy >= 60
                              ? "text-amber-600"
                              : "text-rose-600"
                          }`}
                        >
                          {Math.round(attempt.accuracy)}%
                        </span>
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {attempt.passage_title || "Untitled Passage"}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {attempt.score}/{attempt.total} correct
                          </Badge>
                          {attempt.difficulty && (
                            <Badge className={`text-xs ${getDifficultyColor(attempt.difficulty)}`}>
                              L{attempt.difficulty}
                            </Badge>
                          )}
                          {attempt.time_taken > 0 && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {Math.round(attempt.time_taken / 60)}m
                            </span>
                          )}
                          <span className="text-xs text-slate-400">
                            {new Date(attempt.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/results/${attempt.attempt_id}`);
                        }}
                        className="h-8 px-2 text-xs"
                      >
                        Review
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/practice/${attempt.passage_id}`);
                        }}
                        className="h-8 px-2 text-xs gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Retry
                      </Button>
                    </div>
                    
                    <ChevronRight 
                      className="w-5 h-5 text-slate-400 cursor-pointer ml-2" 
                      onClick={() => navigate(`/results/${attempt.attempt_id}`)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex flex-col items-center justify-center text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-600 font-medium">No practice history yet</p>
                <p className="text-sm text-slate-500 mt-1">Your completed practices will appear here</p>
                <Button 
                  size="sm" 
                  className="mt-4"
                  onClick={() => navigate("/practice")}
                >
                  Start Your First Practice
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DashboardPage;
